/**
 * ButtonInverse — CTA-Primitive für ON-DARK Flächen (UI-D5).
 *
 * Schwester-Komponente zu `<Button>`, dediziert für dunkle Surfaces (seit
 * Redesign S2 `bg-ink-900` statt `bg-brand-green-*`)
 * (BulkActionBar; perspektivisch admin-dark-mode features). User-Decision
 * 2026-05-23: separates Primitive statt `inverse`-Variant am bestehenden
 * Button — saubere Trennung Light- vs Dark-Surface, kein Variants-Bloat
 * im `<Button>`. Bewusste Duplikation der Render-Shell akzeptiert für
 * API-Clarity (kein shared `ButtonShell`).
 *
 * API-Parität mit `<Button>` (Codex spec-eval R1):
 * - `ButtonHTMLAttributes<HTMLButtonElement>`-Extension + `...rest`-Spread
 * - forwardRef-Contract
 * - `loading` setzt `disabled` + `aria-busy`
 * - `className` wird LETZTES angehängt (additive Caller-Utilities)
 *
 * Spinner: nutzt das bestehende `<Spinner>` mit `className="text-brand-cream"` —
 * Spinner's SVG stroke ist `currentColor`, daher kein separates SpinnerInverse.
 */
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Spinner } from './Spinner';

export type ButtonInverseVariant = 'secondary' | 'danger' | 'ghost';
export type ButtonInverseSize = 'sm' | 'md';

export interface ButtonInverseProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonInverseVariant;
  size?: ButtonInverseSize;
  /** Zeigt einen Spinner, erzwingt `disabled` + `aria-busy`. */
  loading?: boolean;
  children: ReactNode;
}

const BASE =
  'inline-flex items-center justify-center gap-2 ' +
  'rounded-md font-semibold transition focus:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-900 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const VARIANT: Record<ButtonInverseVariant, string> = {
  secondary: 'bg-white/10 hover:bg-white/20 focus-visible:ring-white',
  danger: 'bg-danger-600 hover:bg-danger-700 focus-visible:ring-red-300',
  ghost: 'hover:underline focus-visible:ring-white',
};

const SIZE: Record<ButtonInverseSize, string> = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
};

export const ButtonInverse = forwardRef<HTMLButtonElement, ButtonInverseProps>(
  function ButtonInverse(
    {
      variant = 'secondary',
      size = 'sm',
      loading = false,
      disabled,
      className,
      type,
      children,
      ...rest
    },
    ref,
  ) {
    const cls = [BASE, SIZE[size], VARIANT[variant], className ?? '']
      .filter(Boolean)
      .join(' ');

    return (
      <button
        ref={ref}
        type={type ?? 'button'}
        disabled={disabled || loading}
        aria-busy={loading ? true : undefined}
        className={cls}
        {...rest}
      >
        {/* Codex PR-R3 [P2]: children IMMER rendern (Spinner als prefix bei
            loading) — sonst verliert Button den accessible-name während
            loading-State, und das DOM-Layout shiftet. Match Button-Primitive
            (src/client/components/ui/Button.tsx Z.83-85). */}
        {loading && <Spinner size="sm" className="text-brand-cream" />}
        {children}
      </button>
    );
  },
);

ButtonInverse.displayName = 'ButtonInverse';
